import {
  useWatch,
  type Control,
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormSetValue,
} from 'react-hook-form';

import { StateCitySelect } from '@/components/common/StateCitySelect';

interface RhfStateCitySelectProps<T extends FieldValues> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  // Field paths rather than fixed 'state'/'city' names: the same pair is bound
  // to a lead form, an employee form and a nested workflow draft, and only the
  // caller knows what its fields are called.
  stateName: Path<T>;
  cityName: Path<T>;
  portalled?: boolean;
  labelClassName?: string;
  stateClassName?: string;
  cityClassName?: string;
  stateError?: string;
  cityError?: string;
  disabled?: boolean;
}

// Narrowing rather than a cast: `useWatch` is typed by the field path and can
// legitimately yield undefined before the form is reset with its defaults, and
// StateCitySelect is a controlled component that must never be handed one.
const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

/**
 * Bridges StateCitySelect to react-hook-form. `useWatch` subscribes only THIS
 * component to the state/city fields, so the parent form/page doesn't re-render
 * on every keystroke elsewhere in the form.
 */
export const RhfStateCitySelect = <T extends FieldValues>({
  control,
  setValue,
  stateName,
  cityName,
  portalled,
  labelClassName,
  stateClassName,
  cityClassName,
  stateError,
  cityError,
  disabled,
}: RhfStateCitySelectProps<T>) => {
  const state = useWatch({ control, name: stateName });
  const city = useWatch({ control, name: cityName });

  const write = (name: Path<T>, next: string): void => {
    setValue(name, next as PathValue<T, Path<T>>, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <StateCitySelect
      state={asText(state)}
      city={asText(city)}
      onStateChange={(value) => write(stateName, value)}
      onCityChange={(value) => write(cityName, value)}
      portalled={portalled}
      labelClassName={labelClassName}
      stateClassName={stateClassName}
      cityClassName={cityClassName}
      stateError={stateError}
      cityError={cityError}
      disabled={disabled}
    />
  );
};
